import { error } from '@sveltejs/kit'
import Big from 'big.js'
import { get } from 'svelte/store'

import {
	cleanClient,
	cleanInvoice,
	cleanPayment,
	getClientToPost,
	getInvoiceToPost
} from '$lib/cleaners'
import { validDate } from '$lib/datetimes'
import {
	DEFAULT_COLOR,
	makeClient,
	makeDocumentItem,
	makeInvoice,
	makeInvoiceTax,
	makeUUID
} from '$lib/factories'
import { sentryError } from '$lib/functions/sentry'
import { messages } from '$lib/messages'
import { parseInvoice, parsePayments } from '$lib/parsers'
import { apiGetJson, apiPostJson } from '$lib/proxy/apiProxy'
import {
	debounced,
	SemaphoreKey,
	semaphores,
	waitDebouncedFinish
} from '$lib/semaphores'
import { account as account_store } from '$lib/stores/account/account'
import { chooseClient, client as client_store, resetClient } from '$lib/stores/client'
import { makeDocShippingItem } from '$lib/stores/doc_shipping_items/factories'
import { errors as errors_store } from '$lib/stores/errors'
import { getNextInvoiceNumber } from '$lib/stores/invoice/helpers'
import {
	calculated as calculated_invoice_store,
	last_saved_at,
	// setLastSavedAt,
	uncalculated as uncalculated_invoice_store
} from '$lib/stores/invoice/invoice'
import { getItemTaxes } from '$lib/stores/item/manager'
import { payments as payments_store } from '$lib/stores/payments'
import { InitAccTaxes } from '$lib/stores/taxes/taxes'
import type {
	CalculatedInvoice,
	Client,
	DocumentDiscount,
	InvoiceTax,
	Payment
} from '$lib/types/client_types'
import { CustomErrorMgr } from '$lib/types/custom_error'
import { DOC_TYPE } from '$lib/types/document'
import type { ServerClient, ServerInvoice, ServerPayment } from '$lib/types/server_types'

import { type CalculatedDocumentItem, type DocTax, type DocumentItem, getEditedAt } from '../../components/document/doc_types'
import { getCurrentDay, getDateAfterDays } from '../../datetimes'
import { validUUID } from '../../functions/helpers'
import { InvoiceStatus } from '../../types'
import { GetAccTemplateConfig } from '../account_template/manager'
import type { DocShippingItem } from '../doc_shipping_items/types'
import type { Item } from '../item/types'
import { calculateInvoiceOk } from './calculator'

// const debug_issue = '#1325 - '

export async function addPayment(payment: Payment) {
	console.log('manager:addPayment')

	// await forceSaveDocument()
	// await waitSavedDocument()
	await waitDebouncedFinish(SemaphoreKey.Total)

	const invoice = get(calculated_invoice_store)
	const jsonData = await apiPostJson('/payment/create', {
		...cleanPayment(payment),
		invoice_id: invoice.id
	})
	if (!jsonData || jsonData?.error) {
		console.log(`addPayment - Could not add payment: ${jsonData.error.message}`)
		messages.error(`Could not add payment: ${jsonData.error.message}`)
		return
	} 
	
	payments_store.update((existing) => existing.concat(payment))

	//console.log('addPayment - invoiceStatus: ', json.invoice_status	);
	if (jsonData.invoice_status != '') {
		//console.log('addPayment: update invoice store 01');
		uncalculated_invoice_store.set({
			...get(uncalculated_invoice_store),
			status: jsonData.invoice_status
		})
		//console.log('addPayment: update invoice store 02');
	}
}

export async function updatePayment(payment: Payment) {
	console.log('manager:updatePayment')

	const jsonData = await apiPostJson('/payment/update', cleanPayment(payment))
	if (!jsonData || jsonData?.error) {
		messages.error(`Could not update payment ${jsonData.error.message}`)
		// fire reactive to refresh component with existing the values
		payments_store.update((existing) => existing)
		return
	} 
	payments_store.update((existing) => existing.map((p) => (p.id === payment.id ? payment : p)))

	//console.log('updatePayment - invoiceStatus: ', json.invoice_status	);
	if (jsonData.invoice_status != '') {
		//console.log('updatePayment: update invoice store 01');
		uncalculated_invoice_store.set({
			...get(uncalculated_invoice_store),
			status: jsonData.invoice_status
		})
		//console.log('updatePayment: update invoice store 02');
	}
}

export async function deletePayment(payment: Payment) {
	console.log('manager:deletePayment')

	const uncalculated_doc = get(uncalculated_invoice_store)
	const doc_number = uncalculated_doc.number

	const jsonData = await apiPostJson('/payment/delete', {
		id: payment.id,
		invoice_id: payment.invoice_id,
		invoice_number: doc_number
	})
	if (jsonData?.error) {
		messages.error(`Could not delete payment ${jsonData.error.message}`)
		return
	} 

	payments_store.update((existing) => existing.filter((p) => p.id !== payment.id))

	//console.log('deletePayment - invoiceStatus: ', json.invoice_status	);
	if (jsonData.invoice_status != '') {
		//console.log('deletePayment: update invoice store 01');
		uncalculated_invoice_store.set({
			...uncalculated_doc,
			status: jsonData.invoice_status
		})
		//console.log('deletePayment: update invoice store 02');
	}
}

export async function chooseClientAndSaveDocument(client: Client | null = null) {
	console.log('manager:chooseClient')
	chooseClient(client)
	debounced(SemaphoreKey.Invoice, saveDoc)
}


export type Preload = {
	invoice: ServerInvoice
	client: ServerClient
	payments: ServerPayment[]
}

export function preloadInvoice(preload: Preload) {
	console.log('manager:preloadInvoice', preload.invoice.id)
	console.log('manager:preloadInvoice', preload.invoice)

	uncalculated_invoice_store.set(parseInvoice(preload.invoice))
	client_store.set(preload.client)
	payments_store.set(parsePayments(preload.payments))
}

export async function copyInvoice(preload: Preload) {
	const account = get(account_store)	

	// TODO: with new ClientSelector, we can check if can copy client (now is more clear edit or search new)
	// do not copy client (prevent error of edit Client
	// client_store.set(preload.client);
	//client_store.set(makeClient())

	// Check if account is configured to copy the Client
	if (account.invoice_copy_with_client)
		client_store.set(preload.client)
	else
		client_store.set(makeClient())

	const newInvoiceId = makeUUID()
	// const account = get(account_store)
	const nextNumber = await getNextInvoiceNumber('')
	console.log('manager:copyInvoice %s to: %s', preload.invoice.id, newInvoiceId)

	const today = getCurrentDay()
	let calculatedInvoice = parseInvoice(preload.invoice)
	// reset invoice values
	calculatedInvoice = {
		...calculatedInvoice,
		id: newInvoiceId,
		number: nextNumber,
		issued: today,
		due: getDateAfterDays(today, 30),
		status: InvoiceStatus.Draft,
		last_sent_at: null,
		last_viewed_at: null,
		last_paid_at: null,
		last_refunded_at: null,
		last_deleted_at: null
	}

	//-------------------
	// Replace each Doc.TaxId with a new uuid
	calculatedInvoice.taxes.forEach((tax) => {
		const oldTaxId = tax.id
		const newTaxId = makeUUID()
		tax.id = newTaxId
		//
		calculatedInvoice.items.forEach((item: CalculatedDocumentItem) => {
			const idxTax = item.taxes.indexOf(oldTaxId)
			if (idxTax >= 0) {
				// replace doc.tax.id with the new one
				item.taxes.splice(idxTax, 1, newTaxId)
			}

			const bigTaxTotal = item.tax_totals[oldTaxId]
			if (bigTaxTotal) {
				delete item.tax_totals[oldTaxId]
				item.tax_totals[newTaxId] = bigTaxTotal
			}
		})
	})
	calculatedInvoice.items.forEach((item) => {
		// let oldItemId = item.id
		const newItemId = makeUUID()
		item.id = newItemId
		item.edited_at = getEditedAt()
	})
	calculatedInvoice.shipping_items.forEach((item) => {
		// let oldItemId = item.id
		const newItemId = makeUUID()
		item.id = newItemId
		item.doc_id = newInvoiceId
		item.edited_at = getEditedAt()
	})
	//-------------------

	// set store
	uncalculated_invoice_store.set(calculatedInvoice)

	// do not copy payments
	payments_store.set([])
}

export async function resetDocument(options?: ResetInvoiceParam) {
	console.log('manager:resetDocument')
	const account = get(account_store)
	const account_template = await GetAccTemplateConfig(DOC_TYPE.INVOICE, account.template_type)
	// resetAllSemaphores()

	let newNumber = ''
	if (options && options.number && options.number != '' ) {
		newNumber = options.number
	} else {
		newNumber = await getNextInvoiceNumber('')
	}

	let raw_invoice = makeInvoice()

	raw_invoice.number = newNumber
	raw_invoice.original_number = newNumber
	// assign data by default
	raw_invoice.reminder_overdue = account.reminder_invoice_overdue
	raw_invoice.reminder_overdue_days = account.reminder_invoice_overdue_days
	raw_invoice.reminder_overdue_days_1 = account.reminder_invoice_overdue_days_1
	raw_invoice.reminder_overdue_days_2 = account.reminder_invoice_overdue_days_2
	raw_invoice.stub_show_pay_link = account.stub_show_pay_link

	raw_invoice = {
		...raw_invoice,
		// revisit this
		//color: account.color || DEFAULT_COLOR,
		color: account_template.user_config.style.mainAccentColor || account.color || DEFAULT_COLOR,
		number: newNumber,
		heading: account.invoice_default_heading || raw_invoice.heading,
		notes: account.invoice_default_notes || '',
		terms: account.invoice_default_terms || '',
		currency_id: account.currency_id || raw_invoice.currency_id || 'usd',
		language_id: account.language_id || raw_invoice.language_id || 'en-US'
	}

	if (options?.items) {
		const { invoiceItemsNew, invoiceTaxesNew } = await itemsToInvoice(
			options?.items,
			raw_invoice.items,
			raw_invoice.id
		)

		raw_invoice.items = invoiceItemsNew
		raw_invoice.taxes = invoiceTaxesNew
	}

	uncalculated_invoice_store.set(raw_invoice)
	resetClient(options?.client)

	// if(client)
	// 	client_store.set(client);
	// else
	// 	client_store.set(makeClient());

	payments_store.set([])

	// remove this: is not necesary
	// setInvoiceNumber(raw_invoice.number);
}

type ResetInvoiceParam = {
	client?: Client
	items?: Item[]
	number?: string
}

const itemsToInvoice = async (
	items: Item[],
	invoiceItems: DocumentItem[],
	invoiceId: string
): Promise<{ invoiceItemsNew: DocumentItem[]; invoiceTaxesNew: InvoiceTax[] }> => {
	let invoiceItemsNew: DocumentItem[] = []
	let invoiceTaxesNew: InvoiceTax[] = []

	//console.log("----> items: ", items)
	invoiceItemsNew = [
		...items.map((i) =>
			makeDocumentItem({
				item_id: i.id,
				idx: 0,
				name: i.name,
				details: i.details,
				price: i.price,
				quantity: Big(1)
			})
		),
		invoiceItems[0]
	]
	//console.log("----> invoiceItemsNew: ", invoiceItemsNew)

	for (let i = 0; i < invoiceItemsNew.length - 1; i++) {
		// get taxes of item
		const itemTaxes = await getItemTaxes(invoiceItemsNew[i].item_id)
		//console.log("---> itemTaxes: ", itemTaxes)

		// filter taxes to add invoice
		const invoiceTaxesAdd = itemTaxes.filter(
			(x: { tax_id: string }) => invoiceTaxesNew.findIndex((y) => y.tax_id === x.tax_id) === -1
		)
		//console.log("---> invoiceTaxesAdd: ", invoiceTaxesAdd)

		if (invoiceTaxesAdd.length !== 0) {
			// convert itemTax in documentTax
			const docTaxesAdd = invoiceTaxesAdd.map(
				(x: { tax_id: string; name: string; rate: Big.BigSource }, i: number) => {
					const et = makeInvoiceTax({ idx: invoiceTaxesNew.length + i + 1 })
					et.invoice_id = invoiceId
					et.tax_id = x.tax_id
					et.name = x.name
					et.rate = Big(x.rate)
					return et
				}
			)
			invoiceTaxesNew = [...invoiceTaxesNew, ...docTaxesAdd]
			//console.log("---> invoiceTaxesNew: ",invoiceTaxesNew)
		}

		// get invoiceTax.id to add to item
		invoiceItemsNew[i].taxes = invoiceTaxesNew
			.filter((t: InvoiceTax) => itemTaxes.findIndex((it: { tax_id: string }) => it.tax_id === t.tax_id) !== -1)
			.map((t: InvoiceTax) => t.id)

		// console.log("---> invoiceTaxes filter: ",
		// 					invoiceTaxes.filter( (t:InvoiceTax) =>
		// 						itemTaxes.findIndex((it:any) => itemTaxes.id === t.tax_id) !== -1
		// 				))
	}

	//console.log("---> invoiceItemsNew: ", invoiceItemsNew)
	//console.log("---> invoiceTaxesNew: ", invoiceTaxesNew)

	return { invoiceItemsNew, invoiceTaxesNew }
}

// Saves the (calculated then cleaned) invoice to the server, saving the client if necessary
async function saveDoc() {
	console.log('invoice - save()')

	// ===========================================================
	// Wait for dependencies to be saved first
	// await waitDebouncedFinish(SemaphoreKey.Client)
	await waitDebouncedFinish(SemaphoreKey.ClientShippingLocation)
	await waitDebouncedFinish(SemaphoreKey.Tax)


	let client = cleanClient(get(client_store))
	if (!client.id) {
		client_store.update((existing) => ({ ...existing, id: makeUUID() }))
		client = cleanClient(get(client_store))
	}

	//const savedAt = getEditedAt() //Date.now()
	const invoice = cleanInvoice(get(calculated_invoice_store))
	invoice.last_saved_at = last_saved_at


	if (invoice.id === '00000000-0000-0000-0000-000000000000') {
		console.error('invoice.id == 00000000-0000-0000-0000-000000000000')
		return
	}

	const jsonData = await apiPostJson('/invoices/upsert', {
		invoice: getInvoiceToPost(invoice),
		client: getClientToPost(client)
	})

	let errors = {}

	let isRevertNumber = false
	if (CustomErrorMgr.HasErrorInfo(jsonData.error)) {
		if (CustomErrorMgr.IsWarning(jsonData.error)) {
			// if (jsonData.error.id=='invoices_account_id_number_key') {
			if (
				CustomErrorMgr.IsWithID(
					jsonData.error, 
					CustomErrorMgr.ErrorsID.Invoices_account_id_number_key
				)
			) {
				//--- Check if IsNew
				if (get(uncalculated_invoice_store).original_number === '') {
					messages.warning('The invoice number already exists. We added a new one.')
				} else {
					messages.warning('The invoice number already exists. We returned it to the original value.')
				}
				isRevertNumber = true

			} else if (CustomErrorMgr.IsWithID(
				jsonData.error, 
				CustomErrorMgr.ErrorsID.Number_not_blank)) 
			{
				messages.warning('An empty invoice number is not allowed. We returned it to the original value.')
				isRevertNumber = true
			} else {
				messages.warning(jsonData.error.message)
			}
		} else {
			errors = { invoice_number: false }
			if (jsonData.error) {
				messages.error(`Unexpected error: ${jsonData.error.message}`)
			}
		}
	}

	errors_store.set(errors)


	// Check if the current Invoice store correspond to the response of this request
	// (Because could the resetInvoice occur in between)
	const currentInvoiceStore = get(calculated_invoice_store)


	let unexpectedError = false
	if (CustomErrorMgr.HasErrorInfo(jsonData.error) && CustomErrorMgr.IsError(jsonData.error))
		unexpectedError = true

	// if (jsonData.error || json.id !== currentInvoiceStore.id) {
	if (unexpectedError || jsonData.id !== currentInvoiceStore.id) {
		console.log('manager:saveInvoice - the current invoice is not this')
		return
	}

	// set the variable last_saved_at with the savedAt timestamp
	// WARNING provisionally commented
	// setLastSavedAt(savedAt)

	// Trigger the update/calculated only if it si necessary
	// The backEnd reverted the sent docNumber
	if (isRevertNumber) {
		// If there isn't new fetchs, then revert.
		if (get(semaphores).invoice === 1) {
			if (jsonData.current_number !== currentInvoiceStore.number) {
				console.log(
					'invoice.ts - save() - fixing docuemnt number: actual - json/store',
					currentInvoiceStore.number,
					jsonData.current_number
				)

				uncalculated_invoice_store.update((existing) => ({
					...existing,
					number: unexpectedError ? existing.number : jsonData.current_number,
					original_number: unexpectedError ? existing.original_number : jsonData.current_number
				}))
			}
		}
	}

	// #1384 check
	// checks if the invoice document data has correct totals.
	if (!calculateInvoiceOk(invoice)) {
		const message = 'calculated invoice totals not OK:'
		// console.error('#1384 calculated invoice totals error', invoice)
		sentryError(message, invoice)		
	}

}

export async function setDocumentDueDate(date: string) {
	if (validDate(date)) {
		setDocument({ due: date })
	}
}

export async function setDocumentIssuedDate(date: string) {
	if (validDate(date)) {
		setDocument({ due: date })
	}
}


export async function setDocument(updates: object, skipSave = false) {

	if (!updates) {
		console.log('manager:setDocument with undefined updates')
		return
	}

	console.log('manager:setInvoice updates:', updates)
	// const original = get(calculated_invoice_store)
	// console.log('manager:setInvoice original.number:', original.number)

	// Evaluate if the invoice is no longer editable
	// if (original.number === '' || original.number === '0') return
	// if ( original.number === '' ) return


	// console.log('manager:setInvoice original.number:', original.number)
	uncalculated_invoice_store.update((invoice) => ({ ...invoice, ...updates }))

	// const uncalc = get(uncalculated_invoice_store)
	// console.log('manager:setDocument uncalc:', uncalc)

	if (skipSave) return

	debounced(SemaphoreKey.Invoice, saveDoc)
}

calculated_invoice_store.subscribe((invoice) => {
	if (invoice.items.every((item) => item.price.eq(Big(0)))) {
		return
	}
	const account = get(account_store)
	if (account.interacted) {
		return
	}
	account_store.update((account) => ({ ...account, interacted: true }))
	apiPostJson('/account/interacted')
})


export async function forceSaveDocument() {
	return await debounced(SemaphoreKey.Invoice, saveDoc, 0)
}


// ================================================
// moved to invoice.ts where the stores are defined
// ================================================
// account_store.subscribe((account) => {
// 	//console.log('manager:accountUpdated');
// 	// take color and number if the user hasn't specified
// 	// const { user_color } = get(store)
// 	uncalculated_invoice_store.update((existing) => ({
// 		...existing,
// 		//number: user_number || next_number || account.invoice_next_number || DEFAULT_INVOICE_NUMBER,
// 		// color: user_color || account.color || DEFAULT_COLOR
// 		color: account.color || DEFAULT_COLOR

// 	}))
// })

// uncalculated_invoice_store.subscribe(() => hints.set({}))

export async function loadData(id: string) {
	if (!validUUID(id)) {
		throw error(404)
	}

	// TODO check if some change is pending to save => await force save


 
	const theUrl = '/invoices/read/' + id
	const jsonData = await apiGetJson(theUrl)
	// const jsonData = await apiFetchJson(fetch, 'get', theUrl)

	if (!jsonData) return
	if (jsonData?.error) {
		return { errorMsg: jsonData.error }
	}

	const { invoice, payments, client } = jsonData

	preloadInvoice({ invoice, client, payments })
}

// ======================================
// #region Taxes Actions
export async function applyTax(
	document_data: CalculatedInvoice,
	taxes: DocTax[],
	docTax: DocTax,
	taxAdd: boolean
) {
	// const document_data = get(calculated_invoice_store)

	const tax_total: { [k: string]: Big } = {}
	let items: CalculatedDocumentItem[] = []

	for (let i = 0; i < document_data.items.length; i++) {
		const tmpDocItem = document_data.items[i]

		let hasTax = (tmpDocItem.taxes.findIndex((t) => t === docTax.id) !== -1) 
		if (taxAdd && !hasTax){
			tmpDocItem.taxes = [...tmpDocItem.taxes, docTax.id]
			hasTax = true
		}

		if(hasTax) {
			tax_total[docTax.id] = Big(0)
			tmpDocItem.tax_totals = { ...tmpDocItem.tax_totals, ...tax_total }
			tmpDocItem.edited_at = getEditedAt()
		}

		items = [...items, tmpDocItem]
	}

	let shipping_items: DocShippingItem[] = []
	for (let i = 0; i < document_data.shipping_items.length; i++) {
		const tmpShipDocItem = document_data.shipping_items[i]

		let hasTax = (tmpShipDocItem.taxes.findIndex((t) => t === docTax.id) !== -1) 
		if (taxAdd && !hasTax){
			tmpShipDocItem.taxes = [...tmpShipDocItem.taxes, docTax.id]
			hasTax = true
		}

		if(hasTax) {
			tax_total[docTax.id] = Big(0)
			tmpShipDocItem.tax_totals = { ...tmpShipDocItem.tax_totals, ...tax_total }
			tmpShipDocItem.edited_at = getEditedAt()
		}

		shipping_items = [...shipping_items, tmpShipDocItem]
	}	

	await setDocument({ items, shipping_items, taxes })

	// ========================================================================================
	// If the taxName is used only in this doc, the backend process updates the rate of the tax
	// and we need to refresh the accTaxes store data.
	await InitAccTaxes(fetch, true)

	return
}
// #endregion

// ======================================
// #region Discounts Actions
export async function applyDiscounts(
	document_data: CalculatedInvoice,
	docDiscounts: DocumentDiscount[]
) {
	const docItems = document_data.items.map((i) => {
		return { ...i, edited_at: getEditedAt() }
	})
	const docShippingItems = document_data.shipping_items.map((i) => {
		return { ...i, edited_at: getEditedAt() }
	})
	setDocument({items:docItems, shipping_items:docShippingItems, pre_tax_discounts: docDiscounts })
}
// #endregion


// ======================================
// Doc_Shipping_Item actions
export async function AddDocShippingItem(){

	// ==== set new locally ===========
	// if (!doc.shipping_items) doc.shipping_items = []
	const doc = get(uncalculated_invoice_store)
	const newDocShippingItem = makeDocShippingItem({doc_id: doc.id, idx: doc.shipping_items.length + 1 })
	doc.shipping_items.push(newDocShippingItem)
	uncalculated_invoice_store.set(doc)

	await setDocument({ shipping_items: doc.shipping_items })
}
